import { apiError } from "../../../../../utils/formErrors";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, IconButton, CircularProgress, Switch } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { mkConfig, generateCsv, download } from "export-to-csv";

import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import InlineMessage from "../../../../../component/InlineMessage";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../component/listing";
import CycleFormDialog from "./CycleFormDialog";
import { useNotify } from "../../../../../component/NotificationProvider";
import {
  getReviewCycles, updateReviewCycle, getReviewsByCycle,
} from "../../../../../services/performance";
import { tokens, displayFont } from "../../../../../theme";
import {
  setState, lockNote, setSummary, answeredSummary, answerCounts,
  reorder, copyTarget, questionsForCopy, cleanQuestions, isDirty,
  ASKED_OF, ASKED_OF_OPTIONS,
} from "./questionSet";

/**
 * The questions a review cycle asks.
 *
 * Its own screen rather than a panel on the cycle form, because it is
 * configuration and not a dashboard: someone reading cycle progress has no
 * business deleting a question, and whoever writes the questions looks at them
 * twice a year.
 *
 * A set belongs to a cycle. That scoping is the point — one cycle can ask six
 * questions where the last asked four, and a report comparing them is only
 * honest if each set is the words people actually answered. So only a draft is
 * editable, and an open or closed set is a permanent record.
 *
 * Employee and manager questions share one list deliberately. They are one
 * instrument: the pair is what a cycle asks, and separating them into two
 * tables is how somebody writes a manager question and never notices the
 * employee is asked nothing about it.
 */

const Cell = ({ children, sx }) => <Box sx={{ ...bodyCellSx, ...sx }}>{children}</Box>;

const ReviewQuestions = () => {
  const notify = useNotify();
  const [cycles, setCycles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCycle, setNewCycle] = useState(false);
  // set after creating a cycle, so the screen lands on the draft it just made
  const [pendingSelect, setPendingSelect] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    const res = await getReviewCycles().catch(() => null);
    if (res?.success === false || !res) { setFailed(true); setLoading(false); return; }
    const rows = Array.isArray(res?.data) ? res.data : [];
    setCycles(rows);
    // Open on the draft if there is one — that is the only set anybody can act
    // on, and landing on a read-only table invites a fight with the controls.
    setSelectedId((prev) =>
      prev && rows.some((c) => String(c._id) === String(prev))
        ? prev
        : (rows.find((c) => c.status === "draft") || rows[0])?._id || null
    );
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // A cycle created from here is the one the admin came to write, so land on it
  // rather than leaving them on the closed set they were reading.
  useEffect(() => {
    if (!pendingSelect) return;
    const fresh = cycles.find((c) => c.status === "draft");
    if (fresh) { setSelectedId(fresh._id); setPendingSelect(null); }
  }, [cycles, pendingSelect]);

  const cycle = useMemo(
    () => cycles.find((c) => String(c._id) === String(selectedId)) || null,
    [cycles, selectedId]
  );

  // Reseed the editable copy whenever the selection changes, so an abandoned
  // edit on one cycle cannot leak onto another.
  useEffect(() => {
    setDraft((cycle?.questions || []).map((q) => ({ ...q })));
  }, [cycle]);

  // Only for the counts in the header and the lock note. Fails quietly: the
  // questions are readable whether or not we can say how many answered them.
  useEffect(() => {
    let active = true;
    if (!selectedId) { setReviews([]); return undefined; }
    getReviewsByCycle(selectedId)
      .then((r) => { if (active) setReviews(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => { if (active) setReviews([]); });
    return () => { active = false; };
  }, [selectedId]);

  const state = setState(cycle);
  const counts = useMemo(() => answerCounts(reviews), [reviews]);
  const sourceName = useMemo(() => {
    const from = cycle?.questionsMeta?.copiedFrom;
    if (!from) return null;
    const id = String(from?._id || from);
    return cycles.find((c) => String(c._id) === id)?.name || null;
  }, [cycle, cycles]);

  const target = useMemo(() => copyTarget(cycles, selectedId), [cycles, selectedId]);
  // Questions are only ever written on a draft. With no draft anywhere, every
  // set on this screen is a record and there is nothing to edit — which reads
  // as a broken screen unless it says so and offers the way out.
  const anyDraft = useMemo(() => cycles.some((c) => c.status === "draft"), [cycles]);
  const dirty = useMemo(() => isDirty(cycle?.questions || [], draft), [cycle, draft]);

  const setRow = (i, field, value) =>
    setDraft((p) => p.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await updateReviewCycle(cycle._id, { questions: cleanQuestions(draft) });
      if (res?.success === false) throw apiError(res, "Could not save these questions");
      notify.success("Questions saved");
      await load();
    } catch (e) {
      notify.error(e?.message || "Could not save these questions");
    } finally {
      setSaving(false);
    }
  };

  const copyForward = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const res = await updateReviewCycle(target._id, {
        questions: questionsForCopy(cycle.questions || []),
        copiedFrom: cycle._id,
      });
      if (res?.success === false) throw apiError(res, "Could not copy these questions");
      notify.success(`Copied into ${target.name}`);
      await load();
      setSelectedId(target._id);
    } catch (e) {
      notify.error(e?.message || "Could not copy these questions");
    } finally {
      setSaving(false);
    }
  };

  /*
   * The answers, as written. One row per person per question, because a
   * spreadsheet with a column per question breaks the moment two cycles ask
   * different ones — which is the entire reason a set is scoped to a cycle.
   */
  const exportAnswers = () => {
    const byId = new Map((cycle?.questions || []).map((q) => [String(q._id), q]));
    const rows = [];
    reviews.forEach((r) => {
      const who = r.employee ? `${r.employee.fname || ""} ${r.employee.lname || ""}`.trim() : "—";
      [["Employee", r.self], ["Manager", r.manager]].forEach(([side, assessment]) => {
        (assessment?.answers || []).forEach((a) => {
          rows.push({
            Cycle: cycle.name,
            Employee: who,
            AnsweredBy: side,
            Question: byId.get(String(a.question))?.prompt || "(question removed)",
            Answer: a.text || "",
          });
        });
      });
    });
    if (!rows.length) { notify.error("No answers to export yet"); return; }
    const cfg = mkConfig({ useKeysAsHeaders: true, filename: `review-answers-${cycle.name}` });
    download(cfg)(generateCsv(cfg)(rows));
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading="Review questions" />
        <SectionLoader minHeight="50vh" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading="Review questions"
        breadcrumbs={[
          { label: "Performance", href: "/app/performance" },
          { label: "Review questions" },
        ]}
        meta={cycle ? setSummary(cycle, sourceName) : null}
        actions={
          !anyDraft ? (
            <BtnPrimary onClick={() => setNewCycle(true)} sx={{ borderRadius: "8px" }}>
              New cycle
            </BtnPrimary>
          ) : null
        }
      />

      {/* The dead end this screen used to be: with every cycle closed or open,
          nothing was editable, the copy-forward target did not exist either,
          and there was no indication that the next set is written by creating
          the next cycle. */}
      {!anyDraft && cycles.length > 0 && (
        <InlineMessage tone="info" title="No draft cycle to write questions on." sx={{ mb: "1.25rem" }}>
          A set of questions belongs to a cycle, and only a draft can be edited. Create the
          next cycle to write its questions — it starts from the standard set, and you can
          copy an earlier cycle's questions into it from here.
        </InlineMessage>
      )}

      {failed && (
        <InlineMessage tone="danger" title="Couldn't load review cycles." sx={{ mb: "1.25rem" }}>
          <Box component="button" type="button" onClick={load}
            sx={{ font: "inherit", color: "inherit", background: "none", border: 0, p: 0, textDecoration: "underline", cursor: "pointer" }}>
            Try again
          </Box>
        </InlineMessage>
      )}

      {cycles.length === 0 ? (
        <EmptyState
          title="No review cycles yet"
          description="A set of questions belongs to a cycle, so create a cycle before writing its questions."
          actionLabel="New cycle"
          onAction={() => setNewCycle(true)}
        />
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">
          {/* Which set you are looking at. Every cycle is readable; only the
              draft can be changed. */}
          <MainBox sx={{ padding: "1rem", width: { xs: "100%", md: "15rem" }, flex: "none" }}>
            <Stack spacing={0.5}>
              {cycles.map((c) => {
                const s = setState(c);
                const on = String(c._id) === String(selectedId);
                return (
                  <Box
                    key={c._id}
                    component="button"
                    type="button"
                    onClick={() => setSelectedId(c._id)}
                    aria-current={on}
                    sx={{
                      font: "inherit", textAlign: "left", cursor: "pointer",
                      border: 0, borderRadius: "8px", px: "0.75rem", py: "0.625rem",
                      background: on ? tokens.surfaceSubtle : "transparent",
                      "&:hover": { background: tokens.surfaceSubtle },
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                    }}
                  >
                    <Box sx={{ fontSize: "0.875rem", fontWeight: on ? 600 : 500, color: tokens.text }}>
                      {c.name}
                    </Box>
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.125rem" }}>
                      {s.key}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </MainBox>

          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <MainBox sx={{ padding: "1.5rem" }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: "1rem", flexWrap: "wrap" }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ fontFamily: displayFont, fontSize: "1.125rem", fontWeight: 600, color: tokens.text }}>
                    {cycle?.name}
                  </Box>
                  <StatusChip label={state.label} tone={state.tone} />
                </Stack>
                {!state.editable && (
                  <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap", gap: 1.25 }}>
                    {target && (
                      <BtnSecondary onClick={copyForward} disabled={saving} sx={{ borderRadius: "8px" }}>
                        Copy into the {target.name} draft
                      </BtnSecondary>
                    )}
                    <BtnSecondary onClick={exportAnswers} sx={{ borderRadius: "8px" }}>
                      Export answers
                    </BtnSecondary>
                  </Stack>
                )}
              </Stack>

              <InlineMessage tone={state.editable ? "info" : "warning"} sx={{ mb: "1.25rem" }}>
                {lockNote(cycle, counts)}
                {!state.editable && ` ${answeredSummary(counts)}.`}
              </InlineMessage>

              {/* The read-only state is this same table with the controls
                  removed — not a different component — so an open cycle looks
                  exactly like the draft it was. */}
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: "44rem" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: state.editable ? "2.5rem 1fr 9rem 6rem 2.5rem" : "2.5rem 1fr 9rem 6rem", gap: "0.75rem", px: "0.75rem", py: "0.625rem", background: tokens.surface, borderRadius: "8px 8px 0 0" }}>
                    <Box sx={headCellSx}>#</Box>
                    <Box sx={headCellSx}>Prompt</Box>
                    <Box sx={headCellSx}>Answered by</Box>
                    <Box sx={headCellSx}>Required</Box>
                    {state.editable && <Box sx={headCellSx} aria-label="Remove" />}
                  </Box>

                  {draft.length === 0 && (
                    <Box sx={{ p: "1.25rem", fontSize: "0.875rem", color: tokens.textMuted, border: `1px solid ${tokens.border}`, borderTop: 0 }}>
                      This cycle asks no written questions.
                    </Box>
                  )}

                  {draft.map((row, i) => (
                    <Box
                      key={row._id || `new-${i}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: state.editable ? "2.5rem 1fr 9rem 6rem 2.5rem" : "2.5rem 1fr 9rem 6rem",
                        gap: "0.75rem",
                        alignItems: "center",
                        px: "0.75rem",
                        py: "0.625rem",
                        borderBottom: `1px solid ${tokens.border}`,
                      }}
                    >
                      {state.editable ? (
                        /* Buttons rather than a drag handle: order matters and
                           has to be reachable from a keyboard, which a drag
                           target is not. */
                        <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
                          <Stack>
                            <IconButton size="small" disabled={i === 0} aria-label={`Move question ${i + 1} up`}
                              onClick={() => setDraft((p) => reorder(p, i, i - 1))}>
                              <ArrowUpwardIcon sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                            <IconButton size="small" disabled={i === draft.length - 1} aria-label={`Move question ${i + 1} down`}
                              onClick={() => setDraft((p) => reorder(p, i, i + 1))}>
                              <ArrowDownwardIcon sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      ) : (
                        <Cell sx={{ fontVariantNumeric: "tabular-nums", color: tokens.textMuted }}>{i + 1}</Cell>
                      )}

                      {state.editable ? (
                        <TextField
                          size="small" fullWidth multiline value={row.prompt || ""}
                          placeholder="e.g. What did not go well, and what got in the way?"
                          onChange={(e) => setRow(i, "prompt", e.target.value)}
                          inputProps={{ "aria-label": `Question ${i + 1}` }}
                          sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                        />
                      ) : (
                        <Cell>{row.prompt}</Cell>
                      )}

                      {state.editable ? (
                        <TextField
                          select size="small" SelectProps={{ native: true }}
                          value={row.askedOf || "both"}
                          onChange={(e) => setRow(i, "askedOf", e.target.value)}
                          inputProps={{ "aria-label": `Question ${i + 1} answered by` }}
                          sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                        >
                          {ASKED_OF_OPTIONS.map((k) => (
                            <option key={k} value={k}>{ASKED_OF[k].label}</option>
                          ))}
                        </TextField>
                      ) : (
                        <Cell><StatusChip label={ASKED_OF[row.askedOf || "both"].label} tone={ASKED_OF[row.askedOf || "both"].tone} /></Cell>
                      )}

                      {state.editable ? (
                        <Switch
                          size="small"
                          checked={row.required !== false}
                          onChange={(e) => setRow(i, "required", e.target.checked)}
                          inputProps={{ "aria-label": `Question ${i + 1} required` }}
                        />
                      ) : (
                        /* A word, not a dead switch: a toggle nobody can move
                           reads as a broken control rather than a fact. */
                        <Cell sx={{ color: row.required === false ? tokens.textMuted : tokens.text }}>
                          {row.required === false ? "Optional" : "Required"}
                        </Cell>
                      )}

                      {state.editable && (
                        <IconButton size="small" aria-label={`Remove question ${i + 1}`}
                          onClick={() => setDraft((p) => p.filter((_, idx) => idx !== i))}>
                          <CloseIcon sx={{ fontSize: "0.9375rem" }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>

              {state.editable && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: "1.25rem", gap: 1.5, flexWrap: "wrap" }}>
                  <BtnSecondary
                    onClick={() => setDraft((p) => [...p, { prompt: "", askedOf: "both", required: true }])}
                    sx={{ borderRadius: "8px" }}
                  >
                    <AddIcon fontSize="small" sx={{ mr: "0.25rem" }} /> Add a question
                  </BtnSecondary>
                  {/* The save bar appears only once something has changed —
                      the settings pattern the book asks every module to follow. */}
                  {dirty && (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>Not yet saved</Box>
                      <BtnSecondary
                        onClick={() => setDraft((cycle?.questions || []).map((q) => ({ ...q })))}
                        sx={{ borderRadius: "8px" }}
                      >
                        Discard changes
                      </BtnSecondary>
                      <BtnPrimary onClick={saveDraft} disabled={saving} sx={{ borderRadius: "8px" }}>
                        {saving ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Save draft"}
                      </BtnPrimary>
                    </Stack>
                  )}
                </Stack>
              )}
            </MainBox>
          </Box>
        </Stack>
      )}

      <CycleFormDialog
        open={newCycle}
        onClose={() => setNewCycle(false)}
        onDone={() => { setPendingSelect(true); load(); }}
      />
    </Box>
  );
};

export default ReviewQuestions;
